/**
 * RoomChatScreen — chat di gruppo (fan-out). Deriva l'identità usa-e-getta,
 * fa polling di membri+messaggi, invia cifrando per ogni membro. Il creatore
 * può invitare (QR/link/passcode) e bruciare la stanza.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { Frame, Header, Btn, Box, Input } from '../components/Frame';
import { useStore } from '../lib/store';
import { roomIdentity, getMembers, fetchMessages, sendToRoom, inviteToRoom, burnRoom } from '../lib/rooms';
import { containsLink } from '../lib/roomGuards';

export default function RoomChatScreen() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { mnemonic } = useStore();
  const [info, setInfo] = useState(null); // { type, creatorHandle, memberCap, members, iAmCreator }
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  const [invite, setInvite] = useState(null); // { token, passcode, link, qr }
  const [copied, setCopied] = useState(false);
  const idRef = useRef(null);

  const refresh = useCallback(async (id) => {
    try {
      const inf = await getMembers(roomId, id);
      setInfo(inf);
      setMsgs(await fetchMessages({ roomId, identity: id }));
    } catch (e) { setErr(e.message); }
  }, [roomId]);

  useEffect(() => {
    let alive = true, timer;
    (async () => {
      try {
        const id = await roomIdentity(mnemonic, roomId);
        if (!alive) return;
        idRef.current = id;
        await refresh(id);
        timer = setInterval(() => refresh(id), 4000);
      } catch (e) { setErr(e.message); }
    })();
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [roomId, mnemonic, refresh]);

  async function onSend() {
    const t = text.trim();
    if (!t || !idRef.current || !info) return;
    if (containsLink(t)) {
      setErr('I link non sono consentiti nelle stanze: servono per conversare, non per condividere link o media.');
      return;
    }
    setErr(null);
    setText('');
    try {
      await sendToRoom({ roomId, identity: idRef.current, members: info.members, text: t, ttl: 86400 });
      await refresh(idRef.current);
    } catch (e) { setErr(e.message); }
  }

  async function onInvite() {
    try {
      const { token, passcode } = await inviteToRoom({ roomId });
      const link = `${location.origin}/room-join/${token}`;
      const qr = await QRCode.toDataURL(link, { margin: 1, width: 220 });
      setInvite({ token, passcode, link, qr });
    } catch (e) { setErr(e.message); }
  }

  async function onBurn() {
    if (!confirm('🔥 Bruciare la stanza la cancella PER TUTTI, subito e per sempre. Procedere?')) return;
    try { await burnRoom(roomId); navigate('/rooms', { replace: true }); }
    catch (e) { setErr(e.message); }
  }

  const nick = (h) => info?.members.find((m) => m.handle === h)?.nickname || h.slice(0, 6);

  return (
    <Frame
      statusLeft={info?.type === 'work' ? 'STANZA · LAVORO' : 'STANZA · EVENTO'}
      statusRight={info ? `${info.members.length}/${info.memberCap}` : ''}
    >
      <Header title="// Stanza" crumb="◀" onCrumbClick={() => navigate('/rooms')} />
      {err && <Box variant="danger" className="text-[13px] text-red-400 mb-2">{err}</Box>}

      {info?.iAmCreator && (
        <div className="flex gap-2 mb-3">
          <button onClick={onInvite} className="flex-1 border border-ink-dim rounded-lg text-ink text-[13px] py-1.5">＋ Invita</button>
          <button onClick={onBurn} className="flex-1 border border-redphos text-redphos rounded-lg text-[13px] py-1.5">🔥 Brucia</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 mb-3">
        {msgs.length === 0 && <p className="text-ink-dim text-[14px] text-center py-6">nessun messaggio</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[85%] ${m.mine ? 'ml-auto' : ''}`}>
            <div className={`rounded-2xl px-3 py-2 text-[15px] ${m.mine ? 'bg-ink/[0.06] rounded-br-md' : 'bg-amber/[0.05] rounded-bl-md'}`}>
              {!m.mine && <span className="block text-[11px] text-ink-dim mb-0.5">{nick(m.senderHandle)}</span>}
              <span className="text-ink break-words">{m.text ?? <i className="text-ink-dim">[non per te]</i>}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto flex gap-2 pt-2" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        <Input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }} placeholder="messaggio effimero…" />
        <button onClick={onSend} className="shrink-0 border border-ink bg-ink text-black rounded-lg px-4 text-[15px]">▶</button>
      </div>

      {invite && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setInvite(null)}>
          <div className="w-full max-w-sm border border-ink bg-black p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-ink text-glow">Invito alla stanza</p>
            <img src={invite.qr} alt="QR invito" className="mx-auto rounded bg-white p-1" />
            <p className="text-[12px] text-ink-dim break-all select-all">{invite.link}</p>
            {invite.passcode && (
              <p className="text-[14px] text-ink-dim">
                Passcode (dallo a voce, su un canale diverso): <b className="text-ink select-all">{invite.passcode}</b>
              </p>
            )}
            <Btn onClick={() => { navigator.clipboard?.writeText(invite.link); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              {copied ? '✓ Copiato' : '⧉ Copia link'}
            </Btn>
            <Btn variant="ghost" onClick={() => setInvite(null)}>chiudi</Btn>
          </div>
        </div>
      )}
    </Frame>
  );
}
